/**
 * Диалог подтверждения. Один на всю систему.
 *
 * В макете таких диалогов пятнадцать: архивация задачи, восстановление,
 * удаление столбца и комментария, снятие файла, разрыв связи, отзыв
 * приглашения, удаление с доски, удаление аватара, архивация справочника,
 * отмена правки, сброс пароля, смена проекта столбца, отправка приглашения.
 * Все они — одна форма: заголовок, текст, необязательная причина, две кнопки.
 *
 * Поле причины не декоративное: §15.3 требует причину при архивации,
 * а §23.3 записывает её в аудит.
 */

import { useState } from "react";

import { Button } from "./Button";
import { FormField } from "./FormField";
import { Modal } from "./Modal";
import { Textarea } from "./Textarea";

interface ReasonField {
  label: string;
  required?: boolean;
  placeholder?: string;
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  /** Предупреждение о последствиях — показывается заметнее описания. */
  warning?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
  reasonField?: ReasonField;
  loading?: boolean;
  error?: string | null;
  onConfirm: (reason: string | null) => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  warning,
  confirmLabel = "Подтвердить",
  cancelLabel = "Отмена",
  tone = "default",
  reasonField,
  loading = false,
  error,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  // Причина от предыдущего открытия не должна утечь в следующее подтверждение.
  // Сброс во время рендера, а не в эффекте: эффект вызвал бы лишний проход
  // рендера, а React обрабатывает такую подстройку состояния без него.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (!open) {
      setReason("");
      setTouched(false);
    }
  }

  const reasonRequired = reasonField?.required === true;
  const reasonMissing = reasonRequired && reason.trim().length === 0;

  const handleConfirm = () => {
    setTouched(true);
    if (reasonMissing) return;
    onConfirm(reasonField ? reason.trim() : null);
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      dismissable={!loading}
      width="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "primary"}
            onClick={handleConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {description && <p className="text-sm text-muted">{description}</p>}

        {warning && (
          <p className="rounded-md bg-warning-soft px-3 py-2 text-[13px] font-semibold text-warning">
            {warning}
          </p>
        )}

        {reasonField && (
          <FormField
            label={reasonField.label}
            required={reasonRequired}
            error={touched && reasonMissing ? "Укажите причину" : null}
          >
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={reason}
                placeholder={reasonField.placeholder}
                onChange={(event) => setReason(event.target.value)}
                invalid={touched && reasonMissing}
              />
            )}
          </FormField>
        )}

        {error && (
          <p className="text-[13px] font-semibold text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
